import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { ApiClient } from '@/lib/api/client.ts'

type Step = 'email' | 'otp'

interface ForgotPasswordFormProps {
  onBackToSignIn?: () => void
}

export function ForgotPasswordForm({ onBackToSignIn }: ForgotPasswordFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Step state
  const [step, setStep] = useState<Step>('email')

  // Form fields
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // UI state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await ApiClient.forgotPassword(email)

      // Move to OTP step
      setStep('otp')
    } catch (err) {
      console.error('Password reset OTP send error:', err)
      setError(err instanceof Error ? err.message : t('errors.networkError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setError(null)
    setIsLoading(true)

    try {
      await ApiClient.forgotPassword(email)

      // Clear previous OTP entry
      setOtpCode('')
    } catch (err) {
      console.error('Password reset OTP resend error:', err)
      setError(err instanceof Error ? err.message : t('errors.networkError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyAndReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'))
      return
    }

    // Validate password length
    if (newPassword.length < 6) {
      setError(t('validation.passwordTooShort', { min: 6 }))
      return
    }

    setIsLoading(true)

    try {
      // Verify OTP via server
      await ApiClient.verifyOtp(email, otpCode, 'recovery')

      // Update password (user is now authenticated after OTP verification)
      await ApiClient.resetPassword(newPassword)

      // Success!
      setSuccess(true)

      // Navigate to subscriptions after a short delay
      setTimeout(() => {
        navigate('/subscriptions/manage')
      }, 2000)
    } catch (err) {
      console.error('Password reset verification error:', err)
      setError(err instanceof Error ? err.message : t('errors.networkError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setOtpCode('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
  }

  // Success state
  if (success) {
    return (
      <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm">
        <CardHeader className="space-y-1 pb-6">
          <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-[#26247B] to-[#00AAAD] bg-clip-text text-transparent">
            {t('auth.resetPassword.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-[#63C36F] bg-[#E9F1EE]/50">
            <AlertDescription className="text-[#4E8A56]">
              {t('auth.resetPassword.successMessage')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  // Step 1: Email entry
  if (step === 'email') {
    return (
      <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm">
        <CardHeader className="space-y-1 pb-6">
          <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-[#26247B] to-[#00AAAD] bg-clip-text text-transparent">
            {t('forgotPassword.title')}
          </CardTitle>
          <CardDescription className="text-center">
            {t('forgotPassword.otpDescription')}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSendOtp}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive" className="border-[#CD5B52] bg-[#F3E5EA]/50">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground/80">{t('common.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isLoading}
                className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-[#26247B] to-[#5A5595] hover:from-[#5A5595] hover:to-[#26247B] text-white font-medium shadow-lg shadow-[#26247B]/25 transition-all duration-300"
              disabled={isLoading}
            >
              {isLoading ? t('forgotPassword.sending') : t('forgotPassword.sendCode')}
            </Button>

            {onBackToSignIn && (
              <div className="text-sm text-center text-muted-foreground pt-2">
                {t('forgotPassword.rememberPassword')}{' '}
                <button
                  type="button"
                  onClick={onBackToSignIn}
                  className="text-[#26247B] hover:text-[#5A5595] hover:underline font-semibold transition-colors"
                >
                  {t('forgotPassword.backToSignIn')}
                </button>
              </div>
            )}
          </CardFooter>
        </form>
      </Card>
    )
  }

  // Step 2: OTP + password entry
  return (
    <Card className="border-0 shadow-xl bg-card/80 backdrop-blur-sm">
      <CardHeader className="space-y-1 pb-6">
        <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-[#26247B] to-[#00AAAD] bg-clip-text text-transparent">
          {t('forgotPassword.verifyTitle')}
        </CardTitle>
        <CardDescription className="text-center">
          {t('forgotPassword.otpSentTo', { email })}
        </CardDescription>
        {/* Step indicator */}
        <div className="flex justify-center gap-2 pt-2">
          <div className="w-8 h-1 rounded-full bg-[#26247B]" />
          <div className="w-8 h-1 rounded-full bg-[#26247B]" />
        </div>
      </CardHeader>
      <form onSubmit={handleVerifyAndReset}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="border-[#CD5B52] bg-[#F3E5EA]/50">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* OTP Input */}
          <div className="space-y-2">
            <Label htmlFor="otp" className="text-foreground/80">{t('forgotPassword.otpLabel')}</Label>
            <Input
              id="otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder={t('forgotPassword.otpPlaceholder')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              autoComplete="one-time-code"
              disabled={isLoading}
              className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors text-center text-lg tracking-widest"
            />
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-foreground/80">{t('auth.resetPassword.newPassword')}</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder={t('forgotPassword.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              disabled={isLoading}
              className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors"
            />
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-foreground/80">{t('auth.resetPassword.confirmPassword')}</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              disabled={isLoading}
              className="h-11 border-border/50 focus:border-[#26247B] focus:ring-[#26247B]/20 transition-colors"
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4 pt-2">
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-[#26247B] to-[#5A5595] hover:from-[#5A5595] hover:to-[#26247B] text-white font-medium shadow-lg shadow-[#26247B]/25 transition-all duration-300"
            disabled={isLoading || otpCode.length !== 6}
          >
            {isLoading ? t('auth.resetPassword.submittingButton') : t('auth.resetPassword.submitButton')}
          </Button>

          {/* Resend code and back buttons */}
          <div className="flex flex-col gap-2 w-full">
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isLoading}
              className="text-sm text-[#26247B] hover:text-[#5A5595] hover:underline font-semibold transition-colors disabled:opacity-50"
            >
              {t('forgotPassword.resendCode')}
            </button>
            <button
              type="button"
              onClick={handleBackToEmail}
              disabled={isLoading}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              {t('forgotPassword.useAnotherEmail')}
            </button>
          </div>

          {onBackToSignIn && (
            <div className="text-sm text-center text-muted-foreground pt-2">
              {t('forgotPassword.rememberPassword')}{' '}
              <button
                type="button"
                onClick={onBackToSignIn}
                className="text-[#26247B] hover:text-[#5A5595] hover:underline font-semibold transition-colors"
              >
                {t('forgotPassword.backToSignIn')}
              </button>
            </div>
          )}
        </CardFooter>
      </form>
    </Card>
  )
}
